import { syncTimeLogs } from "../api/task-services";
import indexedDBManager from "./IndexedDBService";

class TimeLogsDB {
  constructor() {
    this.DB_NAME = "timeLogs";
    this.STORE_NAME = "journeyLogs";
    this.KEY_PATH = "journeyId";
    this.TIME_THRESHOLD = 1 * 60 * 1000; // 1 minute in milliseconds
    this.initialized = false;
  }

  async getDB() {
    if (!this.initialized) {
      const db = await indexedDBManager.getDBInstance(
        this.DB_NAME,
        this.STORE_NAME,
        this.KEY_PATH
      );
      this.initialized = true;
      return db;
    }
    return indexedDBManager.getDBInstance(
      this.DB_NAME,
      this.STORE_NAME,
      this.KEY_PATH
    );
  }

  /**
   * Check if the time difference is within threshold
   * @param {string} lastUpdatedTime - Last updated time in ISO string
   * @returns {boolean} - True if within threshold, false otherwise
   */
  isWithinTimeThreshold(updatedAt, thresholdMinutes = 5) {
    const lastUpdate = new Date(updatedAt).getTime();
    const now = Date.now();
    const diffMinutes = (now - lastUpdate) / (1000 * 60);
    return diffMinutes < thresholdMinutes;
  }

  /**
   * Calculate session time in seconds
   * @param {string} startTime - Start time in ISO string
   * @param {string} currentTime - Current time in ISO string
   * @returns {number} - Session time in seconds
   */
  calculateSessionTime(startTime, currentTime) {
    const start = new Date(startTime).getTime();
    const current = new Date(currentTime).getTime();
    return Math.floor((current - start) / 1000); // Convert milliseconds to seconds
  }

  /**
   * Create a new time log entry
   * @param {Object} data - The time log data
   * @returns {Object} - New time log entry with sessionTime in seconds
   */
  createTimeLogEntry(data) {
    const currentTime = new Date().toISOString();
    return {
      startTime: currentTime,
      sessionTime: 0, // Initial session time in seconds
      updatedAt: currentTime,
    };
  }

  async saveTimeLogs(timeLogData, isUpdate = false, isResumeOrStart) {
    const db = await this.getDB();
    let result;

    if (isUpdate) {
      result = await db.add(this.STORE_NAME, timeLogData);
    } else {
      try {
        if (!timeLogData || !timeLogData.journeyId) {
          throw new Error("Invalid time log data: journeyId is required");
        }
        const currentTime = new Date().toISOString();

        try {
          const existingLog = await db.get(
            this.STORE_NAME,
            timeLogData.journeyId
          );

          if (existingLog) {
            // Check if within time threshold
            if (
              this.isWithinTimeThreshold(existingLog.updatedAt) &&
              !isResumeOrStart
            ) {
              // Update session time and updatedAt
              const lastTimeLog =
                existingLog.timeLogs[existingLog.timeLogs.length - 1];
              const updatedSessionTime = this.calculateSessionTime(
                lastTimeLog.startTime,
                currentTime
              );

              const updatedTimeLogs = [...existingLog.timeLogs];
              updatedTimeLogs[updatedTimeLogs.length - 1] = {
                ...lastTimeLog,
                sessionTime: updatedSessionTime, // Session time in seconds
                updatedAt: currentTime,
              };

              const updatedLog = {
                ...existingLog,
                updatedAt: currentTime,
                timeLogs: updatedTimeLogs,
              };
              result = await db.update(this.STORE_NAME, updatedLog);
            } else {
              // Create new time log entry after threshold
              const newTimeLog = this.createTimeLogEntry(timeLogData);
              const updatedLog = {
                ...existingLog,
                updatedAt: currentTime,
                timeLogs: [...existingLog.timeLogs, newTimeLog],
              };
              result = await db.update(this.STORE_NAME, updatedLog);
            }
          } else {
            // Create new journey log with initial time log
            const initialTimeLog = this.createTimeLogEntry(timeLogData);
            const newLog = {
              journeyId: timeLogData.journeyId,
              taskType: timeLogData.taskType,
              taskAttributes: timeLogData.taskAttributes,
              timeLogs: [initialTimeLog],
              createdAt: currentTime,
              updatedAt: currentTime,
            };
            result = await db.add(this.STORE_NAME, newLog);
          }
        } catch (error) {
          if (error.name === "DataError") {
            // Handle first-time initialization
            const initialTimeLog = this.createTimeLogEntry(timeLogData);
            const newLog = {
              journeyId: timeLogData.journeyId,
              taskType: timeLogData.taskType,
              taskAttributes: timeLogData.taskAttributes,
              timeLogs: [initialTimeLog],
              createdAt: currentTime,
              updatedAt: currentTime,
            };
            result = await db.add(this.STORE_NAME, newLog);
          } else {
            throw error;
          }
        }
      } catch (error) {
        console.error("Error saving time logs:", error);
        throw error;
      }
    }

    // Call syncTimeLogs after successful database operation
    try {
      console.log("two");
      await syncTimeLogs();
    } catch (syncError) {
      console.error("Error syncing time logs:", syncError);
    }

    return result;
  }

  async getTimeLogs(journeyId) {
    if (!journeyId) {
      throw new Error("journeyId is required");
    }
    const db = await this.getDB();
    return await db.get(this.STORE_NAME, journeyId);
  }

  async getAllTimeLogs() {
    const db = await this.getDB();
    return await db.getAll(this.STORE_NAME);
  }

  async deleteTimeLogs(journeyId) {
    if (!journeyId) {
      throw new Error("journeyId is required");
    }
    const db = await this.getDB();
    return await db.delete(this.STORE_NAME, journeyId);
  }

  async clearAllTimeLogs() {
    const db = await this.getDB();
    return await db.clear(this.STORE_NAME);
  }

  closeDB() {
    this.initialized = false;
    indexedDBManager.closeDB(this.DB_NAME);
  }
}

// Create a singleton instance
const timeLogsDB = new TimeLogsDB();

export default timeLogsDB;
